import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  debounce,
  FormControl,
  InputBase,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Typography,
  useTheme
} from "@mui/material";
import type { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { SearchOutlined } from "@mui/icons-material";

import { RootState } from "../../store/Index";
import { DataGrid } from "../../components/common/Index";
import { ViewReviewDialog } from "./components/ReviewDialog";

import apiService from "../../service/Api";
import { trim } from '../../utils/trim';
import { tokens } from "../../theme";
import { PilotReviewProps } from "./types";
import { AxiosError } from "axios";

interface Params {
  page: number;
  page_size: number;
  search: string | undefined;
};

const CrewRatings: React.FC = () => {
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [crewReviews, setCrewReviews] = useState<PilotReviewProps[] | null>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "five" | "four" | "three" | "two" | "one">("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [openViewReviewDialog, setOpenViewReviewDialog] = useState<boolean>(false);
  const [selectedReview, setSelectedReview] = useState<number | null>(null);

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const primaryColor = colors.primary[400];
  const primaryTextColor = colors.primary[400];
  const secondaryTextColor = colors.neutral[100];
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const fetchRatings = useCallback(
    async () => {
      setLoading(true);
      setError(null);
      setWarning(null);

      try {
        const apiPage = pagination.page + 1;
        const params: Params = {
          page: apiPage,
          page_size: pagination.pageSize,
          search: searchTerm || undefined,
        };

        const response = await apiService.get<{
          count: number;
          results: PilotReviewProps[];
          next: string | null;
          previous: string | null;
        }>("/api/v1/crews/crew-ratings/", params);

        setCrewReviews(response.data.results);
        setRowCount(response.data.count);
      } catch (error) {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.message || "Failed to load reviews. Please try again.";
          setError(errorMessage);
        } else {
          console.log({ error }, 'fetchReviews error');
        }
      } finally {
        setLoading(false);
      }
    },
  [pagination, searchTerm]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  const columns: GridColDef<PilotReviewProps>[] = [
    {
      field: "pilot",
      headerName: "Feedback for",
      flex: 1,
      renderCell: (params) => {
        const summary = params.row.crew_member_summary;
        return (
          <Box sx={{ display: "flex", my: 1, alignItems: "center", gap: 1 }}>
            <Avatar src={summary.profile_picture || ""} sx={{ width: 32, height: 32 }} />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {summary.first_name} {summary.last_name}
              </Typography>
              <Typography variant="caption">ID: {summary.id}</Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "reviewer",
      headerName: "Feedback by",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const summary = params.row.rater_summary;
        return summary ? (
          <Box sx={{ display: "flex", my: 1, alignItems: "center", gap: 1 }}>
            <Avatar src={summary.profile_picture || ""} sx={{ width: 32, height: 32 }} />
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {summary.first_name} {summary.last_name}
              </Typography>
              <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                {summary.role}
              </Typography>
            </Box>
          </Box>
        ) : "N/A";
      },
    },
    {
      field: "stream",
      headerName: "Stream Details",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 1 }}>
          <Typography variant="subtitle2">
            {params.row.stream_detail?.title ? params.row.stream_detail?.title : 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: "comments",
      headerName: "Feedback Details",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Rating value={params.row.rating} size={'small'} precision={0.1} readOnly />
          <Typography variant={'body2'}>{trim(params.row.comment ?? "", 36)}</Typography>
        </Box>
      ),
    },
  ];

  const handleViewReview = () => {
    setOpenViewReviewDialog(true);
  };

  const handleCloseReview = () => {
    setOpenViewReviewDialog(false);
  };

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  // Handling pagination when user changes the pagination model
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPagination(newModel);
  };

  const handleRowClick = (params: GridRowParams<PilotReviewProps>) => {
    setSelectedReview(params.row.id);
    handleViewReview();
  };

  console.log({ filterType });

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ height: "100%", background: colors.primary[900], padding: "24px", borderRadius: 5, boxShadow: 0 }}>
        <Box display={'flex'} alignItems={'center'} mb={1} gap={1}>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box
              sx={{
                backgroundColor: backgroundColor,
                border: '1px solid',
                borderRadius: 2,
                borderColor: 'primary',
                display: "flex",
                alignItems: "center",
                pl: 1,
                flex: 1,
                minWidth: 320,
              }}
            >
              <SearchOutlined sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }} />
              <InputBase
                placeholder="Search by pilot name, stream or job..."
                sx={{ padding: .4, flex: 1, color: primaryTextColor }}
                onChange={(e) => debouncedSetSearchTerm(e.target.value)}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel
                id="ratings-label"
                sx={{ color: primaryTextColor }}
              >
                Ratings
              </InputLabel>
              <Select
                labelId="ratings-label"
                id="ratings-label"
                value={filterType}
                label="Ratings"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{
                  background: backgroundColor,
                  color: primaryTextColor,
                  borderRadius: 2,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: primaryColor,
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="fives">Fives</MenuItem>
                <MenuItem value="fours">Fours</MenuItem>
                <MenuItem value="threes">Threes</MenuItem>
                <MenuItem value="twos">Twos</MenuItem>
                <MenuItem value="ones">Ones</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <CircularProgress color={'primary'} size={16} />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : warning ? (
          <Alert severity="warning">{warning}</Alert>
        ) : (
          <Box>
            {(crewReviews && crewReviews?.length > 0) && (
              <DataGrid
                rows={crewReviews}
                columns={columns}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={pagination}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 15, 20]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor:
                      colors.primary[500],
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor:
                      colors.primary[500],
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.primary[900],
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {/* All Dialogs */}
      <ViewReviewDialog
        open={openViewReviewDialog}
        onClose={handleCloseReview}
        selectedReview={selectedReview}
      />
    </Box>
  );
};

export default CrewRatings;
